/**
 * Class-time preference helpers shared by the post-submit class-times step
 * (`SchedulePreference.js`, control) and the "Program of Interest" step
 * (`ProgramOfInterest.js`, test): the slot labels, the preferred/optional
 * split from the degree's offered slots, the at-least-one-preferred rule and
 * the `POST /campus-app-schedule/:id` body.
 *
 * With the `degree-aware-class-times` flag on, the preferred slots are the
 * ones the degree offers and the rest are "future interest" (optional); the
 * body is `{preferred, optional}`. With it off, all three slots are preferred
 * and the body is the legacy flat `{morning, evening, night}`.
 */
import {
  CLASS_SCHEDULE_TIMES,
  convertETToLocalTime,
  type ClassSchedule,
  type ClassScheduleType,
} from '@/types/ClassScheduleTimes'

export const DEGREE_AWARE_CLASS_TIMES_FLAG = 'degree-aware-class-times'

export const CLASS_TIME_SLOTS: readonly ClassScheduleType[] = [
  'morning',
  'evening',
  'night',
]

export const CLASS_TIMES_REQUIRED_MESSAGE =
  'At least one option must be selected'

export const EMPTY_CLASS_TIMES: ClassSchedule = {
  morning: false,
  evening: false,
  night: false,
}

type ClassTimeValues = Partial<Record<ClassScheduleType, unknown>> | null

/**
 * A slot's label: its name and, outside Sacramento, its hours converted from
 * Eastern Time to the applicant's local time.
 */
export function getClassTimeLabel(
  slot: ClassScheduleType,
  {sacramento = false}: {sacramento?: boolean} = {},
): string {
  const time = CLASS_SCHEDULE_TIMES[slot]
  if (sacramento) {
    return time.label
  }
  return `${time.label} (${convertETToLocalTime(
    time.etStartHour,
  )} - ${convertETToLocalTime(time.etEndHour, true)})`
}

/** The preferred and optional slots for a degree's offered slots. */
export function splitClassTimeSlots(
  classTimesOffered: ClassTimeValues | undefined,
  degreeAware: boolean,
): {preferredSlots: ClassScheduleType[]; optionalSlots: ClassScheduleType[]} {
  const hasAnyOffered =
    !!classTimesOffered &&
    CLASS_TIME_SLOTS.some(slot => classTimesOffered[slot])
  if (!degreeAware || !hasAnyOffered) {
    return {preferredSlots: [...CLASS_TIME_SLOTS], optionalSlots: []}
  }
  return {
    preferredSlots: CLASS_TIME_SLOTS.filter(slot => classTimesOffered[slot]),
    optionalSlots: CLASS_TIME_SLOTS.filter(slot => !classTimesOffered[slot]),
  }
}

/** Whether at least one preferred slot is ticked. */
export function hasPreferredClassTime(
  values: ClassTimeValues | undefined,
  preferredSlots: readonly ClassScheduleType[],
): boolean {
  return preferredSlots.some(slot => !!values?.[slot])
}

/**
 * The schedule endpoint's body for the ticked slots, and the preferred and
 * optional schedules it stands for (for analytics).
 */
export function buildClassTimesPayload(
  values: ClassTimeValues | undefined,
  {
    preferredSlots,
    optionalSlots,
    degreeAware,
  }: {
    preferredSlots: readonly ClassScheduleType[]
    optionalSlots: readonly ClassScheduleType[]
    degreeAware: boolean
  },
): {
  payload: ClassSchedule | {preferred: ClassSchedule; optional: ClassSchedule}
  preferred: ClassSchedule
  optional: ClassSchedule
} {
  const preferred = {...EMPTY_CLASS_TIMES}
  const optional = {...EMPTY_CLASS_TIMES}
  if (degreeAware) {
    preferredSlots.forEach(slot => {
      preferred[slot] = !!values?.[slot]
    })
    optionalSlots.forEach(slot => {
      optional[slot] = !!values?.[slot]
    })
    return {payload: {preferred, optional}, preferred, optional}
  }
  for (const slot of CLASS_TIME_SLOTS) {
    preferred[slot] = !!values?.[slot]
  }
  return {payload: {...preferred}, preferred, optional}
}

function isSchedule(value: unknown): value is ClassTimeValues {
  return !!value && typeof value === 'object'
}

/**
 * The ticks a saved application restores from `GET /campus-app`'s
 * `preferredClassSchedule` and `optionalClassSchedule`: a slot is ticked when
 * either schedule has it. `undefined` when neither is present or none is
 * ticked, so an older backend that does not return them restores nothing.
 */
export function classTimesFromSchedules(
  preferred: unknown,
  optional: unknown,
): ClassSchedule | undefined {
  if (!isSchedule(preferred) && !isSchedule(optional)) {
    return undefined
  }
  const ticks = {...EMPTY_CLASS_TIMES}
  for (const slot of CLASS_TIME_SLOTS) {
    ticks[slot] =
      (isSchedule(preferred) && !!preferred?.[slot]) ||
      (isSchedule(optional) && !!optional?.[slot])
  }
  return CLASS_TIME_SLOTS.some(slot => ticks[slot]) ? ticks : undefined
}

/** Whether two sets of ticks are the same. */
export function sameClassTimes(
  a: ClassTimeValues | undefined,
  b: ClassTimeValues | undefined,
): boolean {
  return CLASS_TIME_SLOTS.every(slot => !!a?.[slot] === !!b?.[slot])
}
